import type { JSX } from 'react';
import { ProcessAccordion } from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import type { IProcessActivity } from '@common/interfaces';
import { ProcessActivityStatus } from '@common/enums';
import { findProcessAction } from '@common/utils/processActivityUtil';
import { PatientEpisodeCategoryCarePathContext } from '@frontend/components/pages/episodeCategory/carePath/patientEpisodeCategoryCarePathContext';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { ProcessActivity } from './processActivity/ProcessActivity';
import { ProcessContext } from './processContext';

export function ProcessActivities(): JSX.Element {
    const { activeProcessAction } = useDefinedContext(PatientEpisodeCategoryCarePathContext);
    const { availableProcessActions, expandedProcessActivityIds, process, setExpandedProcessActivityIds } =
        useDefinedContext(ProcessContext);
    const sortedActivities = useMemo(() => getSortedActivities(process.activities), [process.activities]);

    return (
        <ProcessAccordion
            onValueChange={setExpandedProcessActivityIds}
            value={expandedProcessActivityIds}
        >
            {/* New process activities that have not been saved yet */}
            {activeProcessAction &&
                activeProcessAction.processId === process.id &&
                activeProcessAction.activities
                    ?.filter((processActionActivity) => !processActionActivity.processActivityId)
                    ?.map((processActionActivity) => (
                        <ProcessActivity
                            key={processActionActivity.id}
                            process={process}
                            processActionActivity={processActionActivity}
                            serviceWorkflowStep={activeProcessAction.serviceWorkflowStep}
                        />
                    ))}

            {sortedActivities.map((processActivity) => {
                const processAction =
                    processActivity.status === ProcessActivityStatus.draft
                        ? findProcessAction(processActivity, availableProcessActions)
                        : undefined;

                return (
                    <ProcessActivity
                        key={processActivity.id}
                        process={process}
                        processAction={processAction}
                        processActivity={processActivity}
                        serviceWorkflowStep={processAction?.serviceWorkflowStep}
                    />
                );
            })}
        </ProcessAccordion>
    );
}

function getSortedActivities(activities: IProcessActivity[]): IProcessActivity[] {
    const completeActivities = activities.filter((activity) =>
        [ProcessActivityStatus.complete, ProcessActivityStatus.cancelled].includes(activity.status)
    );
    const sortedCompleteActivities = orderBy(completeActivities, 'performedDatetime', 'desc');
    const draftActivities = activities.filter((activity) => activity.status === ProcessActivityStatus.draft);
    const sortedDraftActivities = orderBy(draftActivities, 'created_at', 'desc');

    return [...sortedDraftActivities, ...sortedCompleteActivities];
}
